import type { ApolloError, OperationVariables } from '@apollo/client';
import type { DocumentNode, GraphQLError } from 'graphql';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { CILogger } from 'common/log/log.provider';
import { CIPageStatus } from 'common/viewcomp/general/ci_pagestatus';

import { CIError } from '../error/error_util';
import { gqlSimpleQueryAsPromise } from '../graphql/utils';

import { usePageStatus } from './use-page-status';

interface State<EntityType extends object> {
    data: EntityType | undefined;
    isLoading: boolean;
    error: GraphQLError | ApolloError | undefined;
}

/*
    Maintained by David Mlnarik. Please add me to review when you modify this file.
 */
export function useMutation<EntityType extends object, VariablesType extends OperationVariables = OperationVariables>(
    query: string | DocumentNode,
    {
        queryString,
        hideError,
        logErrorMessage,
        throwOnError,
        onCompleted,
    }: {
        queryString?: string;
        hideError?: boolean;
        logErrorMessage?: string;
        throwOnError?: boolean;
        onCompleted?: () => void;
    }
) {
    const pageStatus = usePageStatus();
    const { t } = useTranslation();
    const [{ data, isLoading, error }, setState] = useState<State<EntityType>>({
        data: undefined,
        isLoading: false,
        error: undefined,
    });

    async function request(variables: VariablesType, { successMessage }: { successMessage?: string } = {}) {
        setState((state) => (!state.isLoading ? { ...state, isLoading: true } : state));

        try {
            const response = await gqlSimpleQueryAsPromise<EntityType>(query, {
                queryString,
                variables,
            });
            setState({ data: response, isLoading: false, error: undefined });

            if (successMessage) {
                CIPageStatus.success(successMessage);
            }

            if (onCompleted) {
                onCompleted();
            }

            return response;
        } catch (error) {
            const ciError = t(CIError.fromGraphQLError(error)?.translationKey);

            if (!hideError) {
                pageStatus.error(ciError);
            }

            CILogger.getInstance().error({
                message: logErrorMessage || `Failed GraphQL mutation with query string: ${queryString}`,
                ciError: JSON.stringify(ciError),
            });

            setState({
                data: undefined,
                isLoading: false,
                error,
            });

            if (throwOnError) {
                throw error;
            }
        }
    }

    return [request, { data, isLoading, error }] as const;
}
